import { Invitation, Inviter, Registerer, RegistererState, Session, SessionState, URI, UserAgent, UserAgentDelegate, UserAgentState } from "sip.js";
import moment from "moment";

// Actions
import { doGetRamalRequest, doLeaveRamalRequest } from "src/store/call/actions";

// Types
import { ReturnData } from "src/types";

// Models
import { DialerPSTN } from "src/models/pstn-call";
import { playDTMFTone } from "src/helpers";

type DialerEventTypes = "onCallCreated" | "onCallReceived" | "onCallAnswered" | "onCallHangup" | "onServerDisconnect" | "updateStatus" | "onRegistered" | "onUnregistered";

export enum DialerStatus {
  DISCONNECTING,
  DISCONNECTED,
  CONNECTING,
  CONNECTED,
  CALLING,
  INVITED,
  ON_CALL,
  HANGUPING,
  IDLE,
}

type FullApiMediaOptions = {
  remoteAudioEl?: HTMLAudioElement;
  /** constraints default pro getUserMedia (você pode sobrescrever no callTo com deviceId) */
  defaultAudioConstraints?: MediaTrackConstraints;
};

const dialerDomain = import.meta.env.VITE_DIALER_DOMAIN;

// Funções para FULL API do Sip
function makeRemoteStreamFromSession(session: Session): MediaStream | null {
  const sdh: any = (session as any).sessionDescriptionHandler;
  const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
  if (!pc) return null;

  const remoteStream = new MediaStream();
  pc.getReceivers().forEach((r) => {
    if (r.track) remoteStream.addTrack(r.track);
  });
  return remoteStream;
}

function setAudioElementStream(el: HTMLAudioElement, stream: MediaStream) {
  el.srcObject = stream;
  el.autoplay = true;
  // iOS/Safari às vezes precisa:
  // el.play().catch(() => {});
}

async function sendDtmfBestEffort(session: Session, tone: string) {
  playDTMFTone(tone);

  // 1) tenta RTP (RFC4733) via SDH (quando disponível) :contentReference[oaicite:4]{index=4}
  const sdh: any = (session as any).sessionDescriptionHandler;
  if (sdh?.sendDtmf) {
    const ok = sdh.sendDtmf(tone, { duration: 100, interToneGap: 70 });
    if (ok) return;
  }

  // 2) fallback SIP INFO (guia oficial) :contentReference[oaicite:5]{index=5}
  // SIP.js expõe session.info()
  const body = `Signal=${tone}\r\n` + `Duration=100\r\n`;

  // @ts-ignore (tipagem pode variar por versão)
  await (session as any).info({
    contentType: "application/dtmf-relay",
    contentDisposition: "render",
    content: body,
  });
}

export async function createDialerInstance(
  mediaOptions: FullApiMediaOptions | null,
  instanceName: string,
  options: {
    aderenteSlug: string;
    currentInput?: string;
  },
) {
  // Inner Variables
  let userAgent: UserAgent | null = null;
  let registerer: Registerer | null = null;
  let currentSession: Session | null = null;

  // Tratativa para pam do peerConection
  let inviteInFlight: Promise<any> | null = null;
  let inviteAttemptId = 0;
  let canceledAttemptId: number | null = null;
  let lastInviter: Inviter | null = null;

  let isRegistering = false;

  let ramal: { ramal: string; password: string } | null = null;

  // Inner state
  const state: {
    currentCall: DialerPSTN | null;
    currentInput: string;
    call: Array<DialerPSTN>;
    isInputMuted: boolean;
    isOutputMuted: boolean;
  } = {
    currentCall: null,
    currentInput: options.currentInput || "",
    call: [],
    isInputMuted: false,
    isOutputMuted: false,
  };

  // Status
  let dialerStatus = DialerStatus.DISCONNECTED;

  // Some binds
  const binds: Array<{
    ref: number;
    eventName: string;
    callback: Function;
  }> = [];

  // Private Methods
  const onEvent = async (eventName: DialerEventTypes, args: { ramal?: string; instanceName: string; args?: any }, callback?: Function) => {
    if (callback) callback(args);

    const bindHandlers = binds.filter((b) => b.eventName === eventName);

    if (bindHandlers) await Promise.all(bindHandlers.map((b) => b.callback.call(null, args)));
  };

  const updateStatus = (status: DialerStatus, optionalArgs?: any) => {
    dialerStatus = status;
    onEvent("updateStatus", { instanceName, args: { status, ...optionalArgs } });
  };

  // Dealloc ramal
  async function deallocRamal() {
    const dealloc = ramal;

    if (!dealloc) return console.error("Envie um ramal para desassociação");

    const { success, message } = await doLeaveRamalRequest(dealloc.ramal);

    if (!success) return console.error(message);

    ramal = null;

    return true;
  }

  // Status detectors
  const onRegistered = () => {
    isRegistering = false;
    updateStatus(DialerStatus.CONNECTED);
  };
  const onUnregistered = () => {
    isRegistering = false;
    updateStatus(DialerStatus.DISCONNECTED);
  };
  const onCallCreated = () => updateStatus(DialerStatus.CALLING);
  const onCallAnswered = () => {
    if (state.currentCall) {
      state.currentCall.startedAt = moment().utcOffset(-3).format("YYYY-MM-DDTHH:mm:ss");
    }

    return updateStatus(DialerStatus.ON_CALL);
  };
  const onCallHangup = () => {
    state.isInputMuted = false;
    state.isOutputMuted = false;

    updateStatus(DialerStatus.IDLE);
  };

  const onInviteSent = () => {
    updateStatus(DialerStatus.INVITED);
  };

  // Mapa de eventos
  function wireSession(session: Invitation | Inviter, kind: "outgoing" | "incoming") {
    currentSession = session;

    session.stateChange.addListener((newState) => {
      if (newState === SessionState.Establishing) {
        // Outgoing: INVITE enviado / Ringing
        // Incoming: tocando local até accept/reject
        onInviteSent();
      }

      if (newState === SessionState.Established || newState === SessionState.Establishing) {
        const remoteStream = makeRemoteStreamFromSession(session);
        const remoteAudioEl = mediaOptions?.remoteAudioEl || (document.getElementById("remote-dialer-audio") as HTMLAudioElement);

        if (remoteStream && remoteAudioEl) {
          setAudioElementStream(remoteAudioEl, remoteStream);
        }

        if (newState === SessionState.Established) {
          onEvent(
            "onCallAnswered",
            {
              ramal: ramal?.ramal,
              instanceName,
              args: { mediaStream: remoteStream, currentCall: state.currentCall, ramal: ramal?.ramal },
            },
            onCallAnswered,
          );
        }
      }

      if (newState === SessionState.Terminated) {
        onEvent("onCallHangup", { ramal: ramal?.ramal, instanceName, args: { currentCall: state.call.pop() } }, onCallHangup);
        currentSession = null;
        state.currentCall = null;
      }
    });

    if (kind === "incoming") {
      onEvent("onCallReceived", { ramal: ramal?.ramal, instanceName, args: { session, ramal: ramal?.ramal } });
    } else {
      onEvent("onCallCreated", { ramal: ramal?.ramal, instanceName, args: { currentCall: state.currentCall } }, onCallCreated);
    }
  }

  // Exposed functions
  const on = (eventName: DialerEventTypes, callback: Function) => {
    // Create remove ref
    const ref = new Date().getTime();

    // Push list
    binds.push({ ref, eventName, callback });

    return ref;
  };

  const createSimpleUser = async (aderenteSlug: string): Promise<ReturnData> => {
    if (userAgent || registerer) return { success: true, data: { ramal, userAgent, registerer } };

    // Set Status
    updateStatus(DialerStatus.CONNECTING);

    if (!ramal) {
      // Allocate ramal
      const { success, data, message } = await doGetRamalRequest(aderenteSlug || "icom-qa");

      // Deny if error
      if (!success) {
        updateStatus(DialerStatus.DISCONNECTED);
        return { success: false, message };
      }
      // Set ramal
      ramal = { ramal: data.ramal, password: data.password };
    }

    // Set variables
    const webrtcUrl = import.meta.env.VITE_SIP_WS_URL;
    const uri = UserAgent.makeURI(`sip:${ramal.ramal}@${dialerDomain}`);

    const delegate: UserAgentDelegate = {
      onInvite: (invitation: Invitation) => {
        wireSession(invitation, "incoming");
      },
    };

    // Define client
    try {
      userAgent = new UserAgent({
        uri,
        transportOptions: { server: webrtcUrl },
        authorizationUsername: ramal.ramal,
        authorizationPassword: ramal.password,
        logLevel: "debug",
        delegate,
        contactParams: {},
      });

      await userAgent.start();

      registerer = new Registerer(userAgent, {
        logConfiguration: false, // If true, constructor logs the registerer configuration.
        expires: 300,
        refreshFrequency: 75, // Determines when a re-REGISTER request is sent. The value should be specified as a percentage of the expiration time (between 50 and 99).
      });

      registerer.stateChange.addListener((s) => {
        if (s === RegistererState.Registered) {
          onEvent("onRegistered", { ramal: ramal?.ramal, instanceName }, onRegistered);
        }
        if (s === RegistererState.Unregistered) {
          onEvent("onUnregistered", { instanceName }, onUnregistered);
        }
      });

      isRegistering = true;
      await registerer.register();

      return { success: true, data: { userAgent, registerer, ramal: ramal.ramal } };
    } catch (ex: any) {
      updateStatus(DialerStatus.DISCONNECTED, { error: ex });

      return {
        success: false,
        message: ex.message,
      };
    }
  };

  // Call methods
  const updateCurrentNumber = (newNumber: string) => {
    return (state.currentInput = newNumber);
  };

  function isTransportConnected(): boolean {
    const transport: any = (userAgent as any)?.transport;
    return !!transport && (transport.isConnected?.() === true || transport.connected === true);
  }

  async function hardResetConnection(reason?: unknown) {
    try {
      await registerer?.unregister?.().catch(() => {});
    } catch {}

    try {
      await userAgent?.stop?.().catch(() => {});
    } catch {}

    registerer = null;
    userAgent = null;
    currentSession = null;
    lastInviter = null;
    inviteInFlight = null;

    updateStatus(DialerStatus.DISCONNECTED, { reason });
  }

  async function ensureConnected() {
    if (!userAgent || !registerer) {
      const result = await createSimpleUser(options.aderenteSlug);
      if (!result.success) {
        throw new Error(result.message || "Falha ao criar cliente SIP");
      }
    }

    // se o UA existe, mas o transport não está conectado, recrie tudo
    if (!isTransportConnected()) {
      await hardResetConnection("transport_not_connected");

      const result = await createSimpleUser(options.aderenteSlug);
      if (!result.success) {
        throw new Error(result.message || "Falha ao recriar cliente SIP");
      }
    }

    // aqui o UA foi recém-criado ou já estava conectado
    if (!isRegistering) {
      isRegistering = true;
      await registerer!.register();
    }
  }

  const callTo = async (number: string, deviceId?: string) => {
    try {
      await ensureConnected();
    } catch (error) {
      await hardResetConnection(error);
      return {
        success: false,
        message: "Sem conexão com o servidor SIP.",
        error,
      };
    }

    if (currentSession && currentSession.state !== SessionState.Terminated) {
      return { success: false, message: "Já existe uma chamada em andamento." };
    }

    try {
      // Create new call log
      if (state.currentCall) {
        state.currentCall.numberCalled = number;
      } else {
        const currentCall = new DialerPSTN({
          numberCalled: number,
        });

        state.currentCall = currentCall;
        state.call.push(currentCall);
      }

      const myAttempt = ++inviteAttemptId;
      canceledAttemptId = null;

      const targetUri = UserAgent.makeURI(`sip:${number}@${dialerDomain}`) as URI;

      const inviter = new Inviter(userAgent!, targetUri, {
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: deviceId ? (deviceId === "default" ? true : { deviceId: { exact: deviceId } }) : (mediaOptions?.defaultAudioConstraints ?? true),
            video: false,
          },
        },
        earlyMedia: true,
      });

      // Save for later
      lastInviter = inviter;

      // Map events
      wireSession(inviter, "outgoing");

      // IMPORTANT: guarde a promise e trate erro como "cancelamento" quando for o caso
      inviteInFlight = inviter.invite().catch((err) => {
        const wasCanceled = canceledAttemptId === myAttempt || inviter.state === SessionState.Terminated;
        if (wasCanceled) return; // swallow
        throw err;
      });

      await inviteInFlight;
      inviteInFlight = null;

      return { success: true, data: { ramal: ramal?.ramal, currentCall: state.currentCall } };
    } catch (ex) {
      updateStatus(DialerStatus.DISCONNECTED, { error: ex });
      // Dismiss current call and inactivate
      state.currentCall = null;
      currentSession = null;

      return { success: false, error: ex };
    }
  };

  const callHangup = async () => {
    // Salva a instancia de possível status indevido
    if (!currentSession) {
      updateStatus(DialerStatus.IDLE);

      return;
    }

    updateStatus(DialerStatus.HANGUPING);

    const s = currentSession;

    if (inviteInFlight && lastInviter && s === lastInviter) {
      canceledAttemptId = inviteAttemptId;

      try {
        await lastInviter.cancel();
      } finally {
        // Espera o invite terminar para não estourar "Peer connection closed" como unhandled
        await inviteInFlight.catch(() => {});
        inviteInFlight = null;
      }
      return;
    }

    if (s.state === SessionState.Initial || s.state === SessionState.Establishing) {
      // outgoing -> cancel; incoming -> reject
      if (s instanceof Inviter) {
        await s.cancel();
      } else if (s instanceof Invitation) {
        await s.reject();
      } else {
        // fallback (não deveria cair aqui)
        await s.dispose?.();
      }
      return;
    }

    if (s.state === SessionState.Established) {
      await s.bye();
    }
  };

  const sendDTMF = async (key: string) => {
    if (!currentSession) return;
    await sendDtmfBestEffort(currentSession, key);
  };

  const transferCall = (transferRamal: string) => {
    if (!currentSession) return;
    const targetUri = UserAgent.makeURI(`sip:${transferRamal}@${dialerDomain}`);
    if (!targetUri) return;

    return currentSession.refer(targetUri);
  };

  const createCall = (number: string, called?: boolean, externalId?: string) => {
    const newCall = new DialerPSTN({
      numberCalled: number,
      externalId,
    });

    state.call.push(newCall);
    state.currentCall = newCall;
    state.currentInput = newCall.numberCalled!;

    return state.currentCall;
  };

  const toggleInputMute = () => {
    if (!currentSession) return (state.isInputMuted = !state.isInputMuted);

    const sdh: any = (currentSession as any).sessionDescriptionHandler;
    const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
    if (!pc) return (state.isInputMuted = !state.isInputMuted);

    const senders = pc.getSenders().filter((x) => x.track?.kind === "audio");
    const currentlyEnabled = !!senders[0]?.track?.enabled;

    // mute = desabilitar track local
    senders.forEach((s) => {
      if (s.track) s.track.enabled = !currentlyEnabled;
    });

    state.isInputMuted = currentlyEnabled; // se estava enabled, agora mutou
    return state.isInputMuted;
  };

  const toggleOutputMute = () => {
    const stream = currentSession ? makeRemoteStreamFromSession(currentSession) : null;
    if (!stream) return null;

    const track = stream.getAudioTracks()[0];
    if (!track) return null;

    state.isOutputMuted = !state.isOutputMuted;
    track.enabled = !state.isOutputMuted;
    return state.isOutputMuted;
  };

  const disconnectClient = async () => {
    try {
      if (ramal) await deallocRamal();

      if (currentSession && currentSession.state !== SessionState.Terminated) {
        await callHangup();
      }
      currentSession = null;

      if (registerer) await registerer.unregister();
      registerer = null;

      if (userAgent) await userAgent.stop();
      userAgent = null;
    } catch (ex) {
      console.warn("Falha ao encerrar discador");
    }
  };

  const destroy = async () => {
    clearBinds();
    await disconnectClient();
    return true;
  };

  // Helpers methods
  const clearBinds = (bindsArray?: Array<number>) => {
    if (bindsArray)
      return bindsArray.forEach((id) => {
        const index = binds.findIndex((b) => b.ref === id);
        return !!binds.splice(index, 1).length;
      });

    return binds.splice(0, binds.length);
  };

  return {
    // Call controls
    callTo,
    transferCall,
    callHangup,
    createSimpleUser,
    disconnectSimpleUser: disconnectClient,
    updateCurrentNumber,
    sendDTMF,
    toggleInputMute,
    toggleOutputMute,

    // Ultilities
    on,
    clearBinds,
    destroy,
    createCall,

    // Vars
    instanceName,

    // getters
    getStatus: () => dialerStatus,
    getInput: () => state.currentInput,
    getCurrentCall: () => state.currentCall,
    getRamal: () => ramal?.ramal,
    isInputMuted: () => state.isInputMuted,
    isOutputMuted: () => state.isOutputMuted,

    // extra: pra quem quiser usar fora (debug/UI)
    getSession: () => currentSession,
    getRemoteMediaStream: () => (currentSession ? makeRemoteStreamFromSession(currentSession) : null),
  };
}

export type DialerInstance = Awaited<ReturnType<typeof createDialerInstance>>;
